'use client';
import React, { useEffect, useRef } from 'react';

export default function PreviewSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = sectionRef?.current;
    if (el) {
      el?.querySelectorAll('.reveal-up, .reveal-blur, .stagger-grid')?.forEach((child) => observer?.observe(child));
      observer?.observe(el);
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="preview"
      className="relative w-full py-28 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 30% 50%, rgba(26,43,74,0.25) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

          {/* Left: Chapter preview card */}
          <div className="lg:col-span-6 reveal-blur">
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
              }}
            >
              {/* Card top bar */}
              <div
                className="h-12 flex items-center px-5 gap-3 border-b"
                style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}
              >
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                  <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                  <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                </div>
                <span className="font-mono text-[10px] text-foreground/30 uppercase tracking-wider ml-2">
                  Chapter 1 — Introduction
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-[pulse-glow_2s_infinite]" />
                  <span className="font-mono text-[10px] text-foreground/30">Preview</span>
                </div>
              </div>

              {/* Scan line */}
              <div className="relative overflow-hidden">
                <div
                  className="absolute left-0 w-full h-20 animate-sys-scan pointer-events-none z-10"
                  style={{ background: 'linear-gradient(to bottom, transparent, rgba(201,168,76,0.03), transparent)' }}
                />

                {/* Book text */}
                <div className="p-8 space-y-5">
                  <p className="text-foreground/70 text-sm leading-relaxed font-light">
                    In an ever-changing business landscape, successful organizations need tools that help them navigate uncertainty, harness opportunity, and avoid costly missteps. One strategic framework that has stood the test of time is SWOT analysis.
                  </p>
                  <p className="text-foreground/70 text-sm leading-relaxed font-light">
                    Used by Fortune 500 companies, startups, and project teams alike, SWOT provides a structured way to understand the internal and external factors that shape outcomes.
                  </p>

                  {/* Highlighted quote */}
                  <div
                    className="rounded-lg p-4 my-4"
                    style={{ background: 'rgba(201,168,76,0.06)', borderLeft: '3px solid rgba(201,168,76,0.5)' }}
                  >
                    <p className="text-foreground/80 text-sm leading-relaxed italic font-light">
                      &ldquo;The real value of SWOT lies not in filling out a matrix, but in the conversations it sparks and the decisions it informs.&rdquo;
                    </p>
                  </div>

                  <p className="text-foreground/70 text-sm leading-relaxed font-light">
                    SWOT stands for <span className="text-accent font-medium">Strengths</span>, <span className="text-rose-400 font-medium">Weaknesses</span>, <span className="text-blue-400 font-medium">Opportunities</span>, and <span className="text-yellow-400 font-medium">Threats</span>. Strengths and weaknesses are internal factors, reflecting the resources, capabilities, and processes within an organization.
                  </p>

                  <p className="text-foreground/70 text-sm leading-relaxed font-light">
                    When applied thoughtfully, SWOT creates a shared understanding of where you stand today and where you can realistically go next. By combining honest reflection with data-driven insight, SWOT becomes a powerful tool for strategic clarity.
                  </p>

                  {/* Fade out */}
                  <div
                    className="h-16 -mx-8 -mb-8 relative"
                    style={{ background: 'linear-gradient(to bottom, transparent, var(--surface))' }}
                  >
                    <div className="absolute bottom-4 left-8 right-8 flex items-center justify-between">
                      <span className="font-mono text-[10px] text-foreground/25 uppercase tracking-wider">Page 1 of 94</span>
                      <span className="font-mono text-[10px] text-accent/60">Continue reading →</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Text */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            <div>
              <span className="font-mono text-xs text-accent uppercase tracking-widest reveal-up block mb-4">Sample Preview</span>
              <h2 className="font-manrope text-4xl lg:text-5xl font-semibold tracking-tight leading-[0.95] reveal-text-line">
                Start reading
              </h2>
              <h2 className="font-manrope text-4xl lg:text-5xl font-semibold tracking-tight leading-[0.95] text-foreground/30 reveal-text-line" style={{ transitionDelay: '0.1s' }}>
                before you buy.
              </h2>
            </div>

            <div className="reveal-up">
              <a
                href="https://strategyshelf.mysamcart.com/checkout/mastering-swot-analysis-a-practical-guide-for-success"
                className="inline-flex items-center gap-3 btn-download text-background font-manrope font-bold px-7 py-3.5 rounded-xl text-sm"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Get Full Access — $9.99
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}